<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { formatMoneyWithSymbol, formatPriceChange, priceChangeClass } from '@/lib/money'
import { formatCount, formatPct, type InsightsTopDevices } from '@/lib/insights'
import InsightsInfoTip from '@/components/InsightsInfoTip.vue'

/**
 * #11: the five devices with the most active resale listings in the view. Resale
 * only, so a customer without resale competitors doesn't get the table at all.
 */
const props = defineProps<{
    data: InsightsTopDevices
    currencySymbol: string
}>()

const { t, locale } = useI18n()

/** The bar is relative to the leader, so the ranking reads at a glance. */
const maxListings = computed(() => Math.max(1, ...(props.data.devices ?? []).map((device) => device.listings)))

const columns = [
    { key: 'listings', tip: 'topListings' },
    { key: 'share', tip: 'topShare' },
    { key: 'trendAverage', tip: 'topTrend' },
    { key: 'depreciation', tip: 'top60' },
]
</script>

<template>
    <div v-if="data.devices !== null" class="rounded-xl border bg-card">
        <div class="space-y-1 border-b px-6 py-4">
            <h2 class="flex items-center gap-1.5 font-semibold">
                {{ t('insights.top.title') }}
                <InsightsInfoTip :title="t('insights.top.title')" :text="t('insights.tips.top')" />
            </h2>
            <p class="text-xs text-muted-foreground">
                {{ t('insights.top.subtitle', { count: formatCount(data.devices_in_view, locale) }) }}
            </p>
        </div>

        <p v-if="data.devices.length === 0" class="px-6 py-10 text-center text-sm text-muted-foreground">
            {{ t('insights.top.empty') }}
        </p>

        <Table v-else>
            <TableHeader>
                <TableRow>
                    <TableHead class="w-12 pl-6">#</TableHead>
                    <TableHead>{{ t('insights.top.device') }}</TableHead>
                    <TableHead v-for="column in columns" :key="column.key" class="text-right">
                        <span class="inline-flex items-center gap-1">
                            {{ t(`insights.top.${column.key}`) }}
                            <InsightsInfoTip
                                :title="t(`insights.top.${column.key}`)"
                                :text="t(`insights.tips.${column.tip}`)"
                            />
                        </span>
                    </TableHead>
                </TableRow>
            </TableHeader>
            <TableBody>
                <TableRow v-for="device in data.devices" :key="device.rank">
                    <TableCell class="pl-6 tabular-nums text-muted-foreground">{{ device.rank }}</TableCell>
                    <TableCell class="font-medium">{{ device.label }}</TableCell>
                    <TableCell class="text-right">
                        <div class="flex items-center justify-end gap-2">
                            <div class="hidden h-1.5 w-24 overflow-hidden rounded-full bg-muted sm:block">
                                <div
                                    class="h-full rounded-full bg-resell"
                                    :style="{ width: `${(device.listings / maxListings) * 100}%` }"
                                />
                            </div>
                            <span class="w-14 tabular-nums">{{ formatCount(device.listings, locale) }}</span>
                        </div>
                    </TableCell>
                    <TableCell class="text-right tabular-nums">{{ formatPct(device.share_pct, 1) }}</TableCell>
                    <TableCell class="text-right tabular-nums">
                        {{ formatMoneyWithSymbol(device.trend_average, currencySymbol) }}
                    </TableCell>
                    <TableCell
                        class="text-right tabular-nums"
                        :class="priceChangeClass(device.depreciation_60d.value)"
                        :title="device.depreciation_60d.value === null ? t('insights.kpi.notEnoughHistory') : undefined"
                    >
                        {{ formatPriceChange(device.depreciation_60d.value) ?? '—' }}
                    </TableCell>
                </TableRow>
            </TableBody>
        </Table>
    </div>
</template>
